import { useReducer, useEffect } from 'react';
import {
  default as createCommentService
} from '../../../services/commentservice.js';
import { NO_WORKABLE_ENCOUNTERS, ALL_ENCOUNTERS_LOCKED } from '../constants.js';

const UNINITIALIZED_STATUS_VALUE = 0;
const ERROR_STATUS_VALUE = 1;
const INITIALIZED_STATUS_VALUE = 2;

export const PAGE_STATUSES = {
  UNINITIALIZED: UNINITIALIZED_STATUS_VALUE,
  ERROR: ERROR_STATUS_VALUE,
  INITIALIZED: INITIALIZED_STATUS_VALUE
};

const ACTION_TYPES = {
  INITIALIZE_PAGE_REQUEST: 'INITIALIZE_PAGE_REQUEST',
  INITIALZIZE_PAGE_SUCCESS: 'INITIALIZE_PAGE_SUCCESS',
  INITIALIZE_PAGE_FAILURE: 'INITIALIZE_PAGE_FAILURE',
  ACCEPT_ENCOUNTER_LINE_ITEM_REQUEST: 'ACCEPT_ENCOUNTER_LINE_ITEM_REQUEST',
  ACCEPT_ENCOUNTER_LINE_ITEM_SUCCESS: 'ACCEPT_ENCOUNTER_LINE_ITEM_SUCCESS',
  ACCEPT_ENCOUNTER_LINE_ITEM_FAILURE: 'ACCEPT_ENCOUNTER_LINE_ITEM_FAILURE',
  REJECT_ENCOUNTER_LINE_ITEM_REQUEST: 'REJECT_ENCOUNTER_LINE_ITEM_REQUEST',
  REJECT_ENCOUNTER_LINE_ITEM_SUCCESS: 'REJECT_ENCOUNTER_LINE_ITEM_SUCCESS',
  REJECT_ENCOUNTER_LINE_ITEM_FAILURE: 'REJECT_ENCOUNTER_LINE_ITEM_FAILURE',
  COMPLETE_ENCOUNTER_REVIEW_REQUEST: 'COMPLETE_ENCOUNTER_REVIEW_REQUEST',
  COMPLETE_ENCOUNTER_REVIEW_SUCCESS: 'COMPLETE_ENCOUNTER_REVIEW_SUCCESS',
  COMPLETE_ENCOUNTER_REVIEW_FAILURE: 'COMPLETE_ENCOUNTER_REVIEW_FAILURE',
  ADD_ENCOUNTER_COMMENT_REQUEST: 'ADD_ENCOUNTER_COMMENT_REQUEST',
  ADD_ENCOUNTER_COMMENT_SUCCESS: 'ADD_ENCOUNTER_COMMENT_SUCCESS',
  ADD_ENCOUNTER_COMMENT_FAILURE: 'ADD_ENCOUNTER_COMMENT_FAILURE',
  SHOW_ADD_COMMENT_DIALOG: 'SHOW_ADD_COMMENT_DIALOG',
  HIDE_ADD_COMMENT_DIALOG: 'HIDE_ADD_COMMENT_DIALOG',
  SHOW_ACCEPT_REJECT_DIALOG: 'SHOW_ACCEPT_REJECT_DIALOG',
  HIDE_ACCEPT_REJECT_DIALOG: 'HIDE_ACCEPT_REJECT_DIALOG',
};

const ACTIONS = {
  initializePageRequest: () => ({ type: ACTION_TYPES.INITIALIZE_PAGE_REQUEST }),
  initializePageSuccess: result => ({
    type: ACTION_TYPES.INITIALZIZE_PAGE_SUCCESS,
    payload: { ...result }
  }),
  initializePageFailure: error => ({
    type: ACTION_TYPES.INITIALIZE_PAGE_FAILURE,
    error
  }),
  acceptEncounterLineItemRequest: encounterLineItemId => ({
    type: ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_REQUEST,
    payload: {
      encounterLineItemId
    }
  }),
  acceptEncounterLineItemSuccess: result => ({
    type: ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_SUCCESS,
    payload: {
      ...result
    }
  }),
  acceptEncounterLineItemFailure: error => ({
    type: ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_FAILURE,
    error
  }),
  rejectEncounterLineItemRequest: encounterLineItemId => ({
    type: ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_REQUEST,
    payload: {
      encounterLineItemId
    }
  }),
  rejectEncounterLineItemSuccess: result => ({
    type: ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_SUCCESS,
    payload: {
      ...result
    }
  }),
  rejectEncounterLineItemFailure: error => ({
    type: ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_FAILURE,
    error
  }),
  completeEncounterReviewRequest: encounterId => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_REQUEST,
    payload: {
      encounterId
    }
  }),
  completeEncounterReviewSuccess: result => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_SUCCESS,
    payload: {
      ...result
    }
  }),
  completeEncounterReviewFailure: error => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_FAILURE,
    error
  }),
  showAddCommentDialog: () => ({ type: ACTION_TYPES.SHOW_ADD_COMMENT_DIALOG }),
  hideAddCommentDialog: () => ({ type: ACTION_TYPES.HIDE_ADD_COMMENT_DIALOG }),
  addEncounterCommentRequest: () => ({ type: ACTION_TYPES.ADD_ENCOUNTER_COMMENT_REQUEST }),
  addEncounterCommentSuccess: result => ({
    type: ACTION_TYPES.ADD_ENCOUNTER_COMMENT_SUCCESS,
    payload: { ...result }
  }),
  addEncounterCommentFailure: error => ({
    type: ACTION_TYPES.ADD_ENCOUNTER_COMMENT_FAILURE,
    error
  }),
  showAcceptRejectDialog: (selectedLineItem, mode) => ({
    type: ACTION_TYPES.SHOW_ACCEPT_REJECT_DIALOG,
    payload: {
      selectedLineItem,
      mode
    }
  }),
  hideAcceptRejectDialog: () => ({
    type: ACTION_TYPES.HIDE_ACCEPT_REJECT_DIALOG
  })
};

const initialState = {
  status: PAGE_STATUSES.UNINITIALIZED,
  isRequestInProgress: false,
  isAddCommentDialogVisible: false,
  isAcceptRejectDialogVisible: false,
  selectedLineItem: null,
  acceptRejectDialogMode: null,
  statusMessage: '',
  errorMessage: null,
  encounter: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.INITIALIZE_PAGE_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Initializing page'
      };
    }

    case ACTION_TYPES.INITIALZIZE_PAGE_SUCCESS: {
      const { payload } = action;

      return {
        ...state,
        ...payload,
        isRequestInProgress: false,
        status: PAGE_STATUSES.INITIALIZED,
        statusMessage: ''
      };
    }

    case ACTION_TYPES.INITIALIZE_PAGE_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        statusMessage: '',
        errorMessage: error
      };
    }

    case ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Accepting line item'
      };
    }

    case ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_SUCCESS: {
      const { payload } = action;
      const targetLineItemId =
        payload.updatedEncounterLineItem.encounterLineItemId;

      const nextLineItems = state.encounter.lineItems.map(lineItem => {
        if (lineItem.encounterLineItemId === targetLineItemId) {
          return { ...lineItem, ...payload.updatedEncounterLineItem };
        }
        return { ...lineItem };
      });

      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: '',
        encounter: {
          ...state.encounter,
          lineItems: nextLineItems
        }
      };
    }

    case ACTION_TYPES.ACCEPT_ENCOUNTER_LINE_ITEM_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        statusMessage: '',
        errorMessage: error
      };
    }

    case ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Rejecting line item'
      };
    }

    case ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_SUCCESS: {
      const { payload } = action;
      const targetLineItemId =
        payload.updatedEncounterLineItem.encounterLineItemId;

      const nextLineItems = state.encounter.lineItems.map(lineItem => {
        if (lineItem.encounterLineItemId === targetLineItemId) {
          return { ...lineItem, ...payload.updatedEncounterLineItem };
        }
        return { ...lineItem };
      });

      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: '',
        encounter: {
          ...state.encounter,
          lineItems: nextLineItems
        }
      };
    }

    case ACTION_TYPES.REJECT_ENCOUNTER_LINE_ITEM_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: '',
        status: PAGE_STATUSES.ERROR,
        errorMessage: error
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Completing encounter review'
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_SUCCESS: {
      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: '',
        encounter: null
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_REVIEW_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        statusMessage: '',
        errorMessage: error
      };
    }

    case ACTION_TYPES.SHOW_ADD_COMMENT_DIALOG: {
      return {
        ...state,
        isAddCommentDialogVisible: true
      };
    }

    case ACTION_TYPES.HIDE_ADD_COMMENT_DIALOG: {
      return {
        ...state,
        isAddCommentDialogVisible: false
      };
    }

    case ACTION_TYPES.ADD_ENCOUNTER_COMMENT_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Adding comment'
      };
    }

    case ACTION_TYPES.ADD_ENCOUNTER_COMMENT_SUCCESS: {
      const { payload } = action;
      const nextComments = state.encounter.comments.slice();
      nextComments.push(payload);

      return {
        ...state,
        encounter: {
          ...state.encounter,
          comments: nextComments
        },
        isRequestInProgress: false,
        statusMessage: ''
      };
    }

    case ACTION_TYPES.ADD_ENCOUNTER_COMMENT_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        errorMessage: error
      };
    }

    case ACTION_TYPES.SHOW_ACCEPT_REJECT_DIALOG: {
      const { payload } = action;

      return {
        ...state,
        isAcceptRejectDialogVisible: true,
        selectedLineItem: payload.selectedLineItem,
        acceptRejectDialogMode: payload.mode
      };
    }

    case ACTION_TYPES.HIDE_ACCEPT_REJECT_DIALOG: {
      return {
        ...state,
        isAcceptRejectDialogVisible: false,
        selectedLineItem: null,
        acceptRejectDialogMode: null
      };
    }

    default: {
      throw Error('useWorkPageDetailService default case');
    }
  }
};

function useWorkPageDetailService(service, encounterId) {
  const commentService = createCommentService(service.serviceRoutes);
  const [pageState, pageDispatch] = useReducer(reducer, initialState);

  const initializePage = async () => {
    pageDispatch(ACTIONS.initializePageRequest());

    try {
      const jsonResponse = await service.initializePage(encounterId);
      pageDispatch(ACTIONS.initializePageSuccess(jsonResponse.result));
    } catch (err) {
      pageDispatch(ACTIONS.initializePageFailure(err));
    }
  };

  useEffect(() => {
    initializePage();

    return () => {}
  }, [encounterId]);

  const acceptEncounterLineItem = (encounterLineItemId, encounterId, selectedReasonOptionIds) => {
    pageDispatch(ACTIONS.acceptEncounterLineItemRequest(encounterLineItemId));

    return service.acceptEncounterLineItem(encounterLineItemId, encounterId, selectedReasonOptionIds)
    .then(responseJson => {
      const { result } = responseJson;
      pageDispatch(ACTIONS.acceptEncounterLineItemSuccess(result));
      return result;
    })
    .catch(e => pageDispatch(ACTIONS.acceptEncounterLineItemFailure(e)));
  };

  const rejectEncounterLineItem = (
    encounterLineItemId,
    encounterId,
    selectedReasonOptionIds
  ) => {
    pageDispatch(ACTIONS.rejectEncounterLineItemRequest(encounterLineItemId));

    return service.rejectEncounterLineItem(
      encounterLineItemId,
      encounterId,
      selectedReasonOptionIds
    )
    .then(responseJson => {
      const { result } = responseJson;
      pageDispatch(ACTIONS.rejectEncounterLineItemSuccess(result));
      return result;
    })
    .catch(e => pageDispatch(ACTIONS.rejectEncounterLineItemFailure(e)));
  };

  const completeEncounterReview = (encounterId, doRedirect) => {
    pageDispatch(ACTIONS.completeEncounterReviewRequest(encounterId));

    return service.completeEncounterReview(encounterId)
    .then(responseJson => {
      const { result } = responseJson;
      pageDispatch(ACTIONS.completeEncounterReviewSuccess(result));

      if (result.nextEncounterId === NO_WORKABLE_ENCOUNTERS
        || result.nextEncounterId === ALL_ENCOUNTERS_LOCKED) {
        doRedirect(`/Medicare/ClientReview/Landing`);
      } else {
        const targetUrl = `/Medicare/ClientReview/Work/Detail/${result.nextEncounterId}`;
        doRedirect(targetUrl);
      }
      return result;
    })
    .catch(e => pageDispatch(ACTIONS.completeEncounterReviewFailure(e)));
  };

  const openAddCommentDialog = () => {
    pageDispatch(ACTIONS.showAddCommentDialog());
  };

  const closeAddCommentDialog = () => {
    pageDispatch(ACTIONS.hideAddCommentDialog());
  };

  const addCommentToEncounter = (encounterId, commentText) => {
    pageDispatch(ACTIONS.addEncounterCommentRequest());

    return commentService.addComment('ClientReviewEncounter', encounterId, commentText)
      .then(result => {
        pageDispatch(ACTIONS.addEncounterCommentSuccess(result));
        closeAddCommentDialog();
      })
      .catch(e => pageDispatch(ACTIONS.addEncounterCommentFailure(e.toString())));
  };

  const openAcceptRejectDialog = (lineItem, mode) => {
    pageDispatch(ACTIONS.showAcceptRejectDialog(lineItem, mode));
  };

  const closeAcceptRejectDialog = () => {
    pageDispatch(ACTIONS.hideAcceptRejectDialog());
  };

  const boundActions = {
    initializePage,
    acceptEncounterLineItem,
    rejectEncounterLineItem,
    completeEncounterReview,
    openAddCommentDialog,
    closeAddCommentDialog,
    addCommentToEncounter,
    openAcceptRejectDialog,
    closeAcceptRejectDialog
  };

  return [
    pageState,
    boundActions
  ];
}

export { useWorkPageDetailService };
